import React, { useState, useEffect } from 'react';
import { User } from '../types/billing.ts';
import api from '../services/api.ts';

interface UsersListProps {
  onViewUser: (id: string) => void;
}

const UsersList: React.FC<UsersListProps> = ({ onViewUser }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  useEffect(() => {
    loadUsers();
  }, [currentPage]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.billing.getUsers({
        page: currentPage,
        limit: 50
      });
      setUsers(response.users);
      setTotalPages(response.pagination.pages);
    } catch (error) {
      console.error('Error loading users:', error);
      setError('Failed to load users. Please check if the API server is running.');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user =>
    user.user_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.user_email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900 border border-red-700 rounded-lg p-6">
        <div className="flex items-center">
          <svg className="w-6 h-6 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <h3 className="text-lg font-medium text-red-400">Error Loading Users</h3>
            <p className="text-red-300 mt-1">{error}</p>
            <button 
              onClick={loadUsers}
              className="mt-3 px-4 py-2 bg-red-700 text-white rounded hover:bg-red-600 transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center">
        <h1 className="text-2xl sm:text-3xl font-bold text-white mb-2 sm:mb-0">Users Overview</h1>
        <div className="text-sm text-gray-400">
          {filteredUsers.length} users found
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <input
          type="text"
          placeholder="Search users by name or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>
      </div>

      {/* Users Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {filteredUsers.map((user) => (
          <div 
            key={user.user_id} 
            className="bg-gray-800 border border-gray-700 rounded-xl p-4 sm:p-6 hover:bg-gray-700 cursor-pointer transition-all duration-200 hover:border-gray-600"
            onClick={() => onViewUser(user.user_id)}
          >
            <div className="flex items-center space-x-3 mb-4">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
                <span className="text-white font-medium">
                  {user.user_name?.charAt(0).toUpperCase() || user.user_email.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="text-lg font-semibold text-white truncate">
                  {user.user_name || 'Unknown User'}
                </h3>
                <p className="text-sm text-gray-400 truncate">{user.user_email}</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <p className="text-sm text-gray-400">Total Cost</p>
                <p className="text-xl font-bold text-white">${user.total_cost?.toFixed(2) || '0.00'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-400">Credits</p>
                <p className="text-xl font-bold text-white">{user.total_credits?.toFixed(2) || '0.00'}</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-gray-400">Credit Usage</span>
                <span className="text-white">{user.credits_assigned ? ((user.total_credits || 0) / user.credits_assigned * 100).toFixed(1) : 0}%</span>
              </div>
              <div className="w-full bg-gray-700 rounded-full h-2">
                <div 
                  className="bg-blue-400 h-2 rounded-full transition-all duration-300" 
                  style={{ width: user.credits_assigned ? `${Math.min((user.total_credits || 0) / user.credits_assigned * 100, 100)}%` : '0%' }}
                ></div>
              </div>
              <div className="flex justify-between text-xs text-gray-500">
                <span>{user.credits_remaining?.toFixed(2) || '0.00'} remaining</span>
                <span>{user.project_count || 0} projects</span>
              </div>
            </div>

            {user.last_activity && (
              <div className="mt-3 pt-3 border-t border-gray-700">
                <p className="text-xs text-gray-400">
                  Last activity: {new Date(user.last_activity).toLocaleDateString()}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center items-center space-x-2">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Previous
          </button>
          <span className="text-white">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="px-3 py-2 bg-gray-800 border border-gray-700 rounded text-white hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Next
          </button>
        </div>
      )}

      {filteredUsers.length === 0 && !loading && (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-gray-400">No users found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {users.length === 0 ? 'No users available in the system.' : 'Try adjusting your search criteria.'}
          </p>
        </div>
      )}
    </div>
  );
};

export default UsersList;